import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Clock, Warning, ShieldWarning, X, ArrowRight,
  CheckCircle, XCircle, ClockCounterClockwise,
} from "@phosphor-icons/react";

/**
 * TenantHistoryPanel — slide-out drawer showing every prior WO at this
 * (address, unit) and a bold WARRANTY warning when any completed job in the
 * last 90 days overlaps by topic keywords with the current job.
 */
export default function TenantHistoryPanel({ job, role = "worker" }) {
  const [data, setData] = useState({ history: [], warranty_matches: [], count: 0, warranty_count: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jobs/${job.id}/history`);
      setData(r.data || { history: [], warranty_matches: [] });
    } catch (e) {
      setData({ history: [], warranty_matches: [], count: 0, warranty_count: 0 });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);

  const detailBase = role === "admin" ? "/admin/jobs" : "/worker/jobs";
  const hasWarranty = (data.warranty_matches || []).length > 0;
  const historyCount = (data.history || []).length;

  if (loading && !historyCount) return null;
  if (!historyCount && !hasWarranty) return null;

  return (
    <>
      {/* Warranty banner (always visible on the job page when triggered) */}
      {hasWarranty && (
        <div className="mt-3 p-3 border-2 border-orange-500 bg-orange-50" data-testid="warranty-banner">
          <div className="flex items-start gap-2">
            <ShieldWarning weight="fill" size={20} className="text-orange-600 flex-shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="font-black uppercase tracking-widest text-xs text-orange-800">
                Warranty callback? {data.warranty_matches.length} similar job{data.warranty_matches.length === 1 ? "" : "s"} closed in the last 90 days at this unit
              </div>
              <ul className="mt-1.5 space-y-1.5">
                {data.warranty_matches.slice(0, 3).map(m => (
                  <li key={m.id}>
                    <Link to={`${detailBase}/${m.id}`} data-testid={`warranty-match-${m.id}`}
                      className="flex flex-col gap-0.5 hover:underline">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center h-4 px-1 bg-[#0f172a] text-white text-[9px] font-black uppercase tracking-widest font-mono flex-shrink-0">
                          WO# {m.wo || m.id.slice(-6)}
                        </span>
                        <span className="font-black text-sm break-words min-w-0">{m.title}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-slate-600">
                        <span>closed {m.days_ago}d ago</span>
                        {m.overlap && m.overlap.length > 0 && (
                          <span className="font-black uppercase tracking-widest text-orange-700">
                            · match: {m.overlap.slice(0, 2).join(", ")}
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-slate-600 italic">
                Consider treating this as a callback (non-billable) instead of a fresh job.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* History button (opens slide-out) */}
      {historyCount > 0 && (
        <button onClick={() => setOpen(true)}
          data-testid="tenant-history-btn"
          className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-50 font-black uppercase text-[11px] tracking-widest">
          <ClockCounterClockwise weight="bold" size={14}/> Unit History · {historyCount}
        </button>
      )}

      {/* Slide-out drawer */}
      {open && (
        <div className="fixed inset-0 z-[110] bg-black/50" onClick={() => setOpen(false)}>
          <div className="absolute top-0 right-0 h-full w-full max-w-md bg-white border-l-4 border-[#0f172a] shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="tenant-history-panel">
            <div className="sticky top-0 p-4 border-b-2 border-slate-200 bg-white flex items-center justify-between z-10">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unit History</div>
                <div className="font-display font-black text-lg truncate">{data.address}{data.unit ? ` · #${data.unit}` : ""}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{historyCount} prior work order{historyCount === 1 ? "" : "s"}</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1"><X weight="bold" size={22}/></button>
            </div>
            <div className="p-4 space-y-2">
              {data.history.map(h => {
                const isCompleted = h.status === "completed";
                const isCanceled = h.status === "canceled";
                const isMerged = h.status === "merged";
                return (
                  <Link key={h.id} to={`${detailBase}/${h.id}`}
                    data-testid={`history-row-${h.id}`}
                    className="block p-3 border-2 border-slate-200 hover:border-[#0f172a] hover:bg-slate-50 group">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
                        WO# {h.wo || h.id.slice(-6)}
                      </span>
                      {h.life_safety && (
                        <span className="inline-flex items-center gap-1 h-5 px-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">
                          <Warning weight="fill" size={10}/> Safety
                        </span>
                      )}
                      {isCompleted && <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-black uppercase tracking-widest"><CheckCircle weight="fill" size={11}/> Completed</span>}
                      {isCanceled && <span className="inline-flex items-center gap-1 text-red-700 text-[10px] font-black uppercase tracking-widest"><XCircle weight="fill" size={11}/> Canceled</span>}
                      {isMerged && <span className="inline-flex items-center text-slate-500 text-[10px] font-black uppercase tracking-widest">Merged</span>}
                      {!isCompleted && !isCanceled && !isMerged && (
                        <span className="inline-flex items-center gap-1 text-slate-700 text-[10px] font-black uppercase tracking-widest"><Clock weight="fill" size={11}/> {h.status}</span>
                      )}
                    </div>
                    <div className="mt-1 font-black text-sm truncate">{h.title}</div>
                    <div className="mt-0.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Created {(h.created_at || "").slice(0, 10)}
                      {h.completed_at && ` · Closed ${h.completed_at.slice(0, 10)}`}
                      {h.assigned_to_name && ` · ${h.assigned_to_name}`}
                    </div>
                    <ArrowRight weight="bold" size={14} className="mt-1 text-slate-300 group-hover:text-[#0f172a]"/>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
